import {
  AgentBrandScope,
  assignConnectionsToBrand as assignConnections,
  BrandPrototypeData,
  BrandRecord,
  BrandRole,
  cloneBrandPrototypeData,
  INITIAL_BRAND_PROTOTYPE_DATA,
  removeBrandMembership,
  updateBrandRecord,
  updateConnectionBrand,
  setBrandMembership,
  deleteBrandRecord,
  updateConnectionAccessGrant,
  updateExternalConnectionBrand,
  updateExternalConnectionBrandLabels,
  updateExternalGoogleAdsCustomerBrand,
  BrandConnectionAccessLevel,
  CreateBrandConnectionInput,
  createBrandConnection,
  disconnectBrandConnection,
  PendingBrandConnectionAccessGrant,
  reconcilePendingInvitationAccessGrants,
  setPendingInvitationAccessGrants,
} from "@/components/Settings/components/brandPrototypeModel";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminProductFeatures } from "@/utils/adminProductFeatures";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  readWorkspaceBrandPrototypeData,
  writeWorkspaceBrandPrototypeData,
} from "./brandPrototypeStorage";

type CreateBrandInput = Pick<
  BrandRecord,
  "name" | "domain" | "market" | "description" | "logoUrl"
> & {
  scope?: "workspace" | "private";
};
type UpdateBrandInput = Pick<
  BrandRecord,
  "name" | "domain" | "market" | "description" | "logoUrl"
>;

type BrandPrototypeContextValue = {
  data: BrandPrototypeData;
  createBrand: (input: CreateBrandInput, createdByUserId: string) => string;
  updateBrand: (brandId: string, input: UpdateBrandInput) => void;
  deleteBrand: (brandId: string) => void;
  assignConnectionsToBrand: (
    brandId: string,
    connectionIds: string[],
    assignedByUserId: string,
  ) => void;
  assignConnectionToBrand: (
    connectionId: string,
    brandIds: string[],
    assignedByUserId: string,
  ) => void;
  setBrandMembership: (
    brandId: string,
    userId: string,
    role: BrandRole,
  ) => void;
  removeBrandMember: (brandId: string, userId: string) => void;
  updateConnectionAccess: (
    brandId: string,
    connectionId: string,
    userId: string,
    accessLevel: BrandConnectionAccessLevel | null,
    canManageAccess?: boolean,
  ) => void;
  setPendingInvitationAccess: (
    email: string,
    grants: Array<
      Pick<
        PendingBrandConnectionAccessGrant,
        "brandId" | "connectionId" | "accessLevel" | "canManageAccess"
      >
    >,
  ) => void;
  assignExternalConnectionToBrand: (
    connectionKey: string,
    brandIds: string[],
    businessGroupLabel?: string,
  ) => void;
  assignExternalGoogleAdsCustomerToBrand: (
    customerKey: string,
    brandIds: string[],
  ) => void;
  createBrandConnection: (
    brandId: string,
    input: CreateBrandConnectionInput,
    connectedByUserId: string,
  ) => void;
  disconnectBrandConnection: (connectionId: string) => void;
  selectAgentBrand: (brandId: AgentBrandScope) => void;
  resetPrototype: () => void;
};

const BrandPrototypeContext = createContext<BrandPrototypeContextValue | null>(
  null,
);

const WorkspaceBrandPrototypeProvider = ({
  children,
  workspaceId,
  workspaceUsers,
}: {
  children: ReactNode;
  workspaceId: string | null;
  workspaceUsers: Array<{ id: string; email: string }>;
}) => {
  const [data, setData] = useState<BrandPrototypeData>(() =>
    readWorkspaceBrandPrototypeData(workspaceId),
  );

  useEffect(() => {
    if (workspaceId) {
      writeWorkspaceBrandPrototypeData(workspaceId, data);
    }
  }, [data, workspaceId]);

  useEffect(() => {
    setData((previous) =>
      reconcilePendingInvitationAccessGrants(previous, workspaceUsers),
    );
  }, [workspaceUsers]);

  const createBrand = useCallback(
    (input: CreateBrandInput, createdByUserId: string): string => {
      const idBase = input.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const id = `${idBase || "brand"}-${Date.now()}`;

      setData((previous) => ({
        ...previous,
        brands: [
          ...previous.brands,
          {
            id,
            isDefault: input.scope === "private" ? false : previous.brands.length === 0,
            scope: input.scope || "workspace",
            name: input.name,
            domain: input.domain,
            market: input.market,
            description: input.description,
            logoUrl: input.logoUrl,
            accent: "#2563EB",
            createdByUserId,
            primaryOwnerUserId: createdByUserId,
          },
        ],
        memberships: [
          ...previous.memberships,
          // The creator starts as the accountable Brand owner. This is scoped
          // to the Brand and does not change their Workspace role.
          { brandId: id, userId: createdByUserId, role: "Owner" },
        ],
      }));

      return id;
    },
    [],
  );

  const deleteBrand = useCallback((brandId: string) => {
    setData((previous) => deleteBrandRecord(previous, brandId));
  }, []);

  const updateBrand = useCallback(
    (brandId: string, input: UpdateBrandInput) => {
      setData((previous) => updateBrandRecord(previous, brandId, input));
    },
    [],
  );

  const assignConnectionsToBrand = useCallback(
    (brandId: string, connectionIds: string[], assignedByUserId: string) => {
      const occurredAt = new Date().toISOString();
      setData((previous) =>
        assignConnections(
          previous,
          brandId,
          connectionIds,
          assignedByUserId,
          occurredAt,
        ),
      );
    },
    [],
  );

  const assignConnectionToBrand = useCallback(
    (connectionId: string, brandIds: string[], assignedByUserId: string) => {
      const occurredAt = new Date().toISOString();
      setData((previous) =>
        updateConnectionBrand(
          previous,
          connectionId,
          brandIds,
          assignedByUserId,
          occurredAt,
        ),
      );
    },
    [],
  );

  const setBrandMembershipForUser = useCallback(
    (brandId: string, userId: string, role: BrandRole) => {
      setData((previous) =>
        setBrandMembership(previous, brandId, userId, role),
      );
    },
    [],
  );

  const removeBrandMember = useCallback((brandId: string, userId: string) => {
    setData((previous) => removeBrandMembership(previous, brandId, userId));
  }, []);

  const updateConnectionAccess = useCallback(
    (
      brandId: string,
      connectionId: string,
      userId: string,
      accessLevel: BrandConnectionAccessLevel | null,
      canManageAccess?: boolean,
    ) => {
      setData((previous) =>
        updateConnectionAccessGrant(
          previous,
          brandId,
          connectionId,
          userId,
          accessLevel,
          canManageAccess,
        ),
      );
    },
    [],
  );

  const setPendingInvitationAccess = useCallback(
    (
      email: string,
      grants: Array<
        Pick<
          PendingBrandConnectionAccessGrant,
          "brandId" | "connectionId" | "accessLevel" | "canManageAccess"
        >
      >,
    ) => {
      setData((previous) =>
        setPendingInvitationAccessGrants(previous, email, grants),
      );
    },
    [],
  );

  const assignExternalConnectionToBrand = useCallback(
    (connectionKey: string, brandIds: string[], businessGroupLabel = '') => {
      setData((previous) =>
        updateExternalConnectionBrandLabels(
          updateExternalConnectionBrand(previous, connectionKey, brandIds),
          connectionKey,
          brandIds,
          businessGroupLabel,
        ),
      );
    },
    [],
  );

  const assignExternalGoogleAdsCustomerToBrand = useCallback(
    (customerKey: string, brandIds: string[]) => {
      setData((previous) =>
        updateExternalGoogleAdsCustomerBrand(previous, customerKey, brandIds),
      );
    },
    [],
  );

  const createBrandScopedConnection = useCallback(
    (
      brandId: string,
      input: CreateBrandConnectionInput,
      connectedByUserId: string,
    ) => {
      const occurredAt = new Date().toISOString();
      setData((previous) =>
        createBrandConnection(
          previous,
          brandId,
          input,
          connectedByUserId,
          occurredAt,
        ),
      );
    },
    [],
  );

  const disconnectScopedConnection = useCallback((connectionId: string) => {
    setData((previous) => disconnectBrandConnection(previous, connectionId));
  }, []);

  const selectAgentBrand = useCallback((brandId: AgentBrandScope) => {
    setData((previous) => ({
      ...previous,
      activeAgentBrandId: brandId,
    }));
  }, []);

  const resetPrototype = useCallback(() => {
    setData(cloneBrandPrototypeData(INITIAL_BRAND_PROTOTYPE_DATA));
  }, []);

  const value = useMemo<BrandPrototypeContextValue>(
    () => ({
      data,
      createBrand,
      updateBrand,
      deleteBrand,
      assignConnectionsToBrand,
      assignConnectionToBrand,
      setBrandMembership: setBrandMembershipForUser,
      removeBrandMember,
      updateConnectionAccess,
      setPendingInvitationAccess,
      assignExternalConnectionToBrand,
      assignExternalGoogleAdsCustomerToBrand,
      createBrandConnection: createBrandScopedConnection,
      disconnectBrandConnection: disconnectScopedConnection,
      selectAgentBrand,
      resetPrototype,
    }),
    [
      assignConnectionToBrand,
      assignConnectionsToBrand,
      createBrand,
      data,
      deleteBrand,
      removeBrandMember,
      updateConnectionAccess,
      setPendingInvitationAccess,
      assignExternalConnectionToBrand,
      assignExternalGoogleAdsCustomerToBrand,
      createBrandScopedConnection,
      disconnectScopedConnection,
      resetPrototype,
      selectAgentBrand,
      setBrandMembershipForUser,
      updateBrand,
    ],
  );

  return (
    <BrandPrototypeContext.Provider value={value}>
      {children}
    </BrandPrototypeContext.Provider>
  );
};

export const BrandPrototypeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { currentWorkspace, workspaceMembers } = useAuth();
  const brandManagementEnabled = getAdminProductFeatures().brandManagement;
  const workspaceId = brandManagementEnabled
    ? currentWorkspace?.id?.trim() || null
    : null;
  const workspaceUsers = useMemo(
    () =>
      (brandManagementEnabled ? workspaceMembers.members : [])
        .filter((member) => member.status === "ACTIVE")
        .map((member) => ({ id: member.userId, email: member.email })),
    [brandManagementEnabled, workspaceMembers.members],
  );

  return (
    <WorkspaceBrandPrototypeProvider
      key={workspaceId || "no-workspace"}
      workspaceId={workspaceId}
      workspaceUsers={workspaceUsers}
    >
      {children}
    </WorkspaceBrandPrototypeProvider>
  );
};

export const useBrandPrototype = (): BrandPrototypeContextValue => {
  const context = useContext(BrandPrototypeContext);
  if (!context) {
    throw new Error(
      "useBrandPrototype must be used inside BrandPrototypeProvider",
    );
  }
  return context;
};
